import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Canonical brand constants — kept consistent across the entire site
 * so that generative and traditional search engines recognise the entity
 * "Next in Luxury" with a stable name, description and imagery.
 */
export const SITE_URL = "https://www.nextinluxury.com";
export const SITE_NAME = "Next in Luxury";
export const BRAND_TAGLINE = "The Art of Becoming Exceptional";
export const DEFAULT_DESCRIPTION =
  "Next in Luxury is an international luxury advisory house delivering strategic consulting, executive training and expert advisory across luxury hospitality, retail, brand strategy, culture, tourism and exceptional experiences.";
export const DEFAULT_OG_IMAGE =
  "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/j0je1nsj_NIL-Gold1.png";

/**
 * SEO component — centralises meta title, description, canonical, Open Graph,
 * Twitter Card and JSON-LD structured data injection for each page.
 *
 * It renders no visible markup and does not affect the DOM tree of the page,
 * so it can be added to any page without altering visuals or copy.
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  schema,
  noindex = false,
}) {
  const fullTitle = title
    ? `${title} — ${SITE_NAME}`
    : `${SITE_NAME} — ${BRAND_TAGLINE}`;
  const url = `${SITE_URL}${path}`;

  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && (
        <meta
          name="robots"
          content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/* -------------------------------------------------------------------------- */
/* Schema builders — reusable helpers to keep structured data consistent      */
/* across the entire site and aligned with schema.org expectations.           */
/* -------------------------------------------------------------------------- */

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "ProfessionalService"],
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: "NIL",
  legalName: "Next in Luxury",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: DEFAULT_OG_IMAGE,
    width: 512,
    height: 512,
  },
  image: DEFAULT_OG_IMAGE,
  slogan: BRAND_TAGLINE,
  description: DEFAULT_DESCRIPTION,
  foundingDate: "2024",
  founder: { "@type": "Person", name: "Next in Luxury" },
  areaServed: [
    { "@type": "Place", name: "Worldwide" },
    { "@type": "Place", name: "Europe" },
    { "@type": "Place", name: "Middle East" },
    { "@type": "Place", name: "North America" },
    { "@type": "Place", name: "Asia" },
  ],
  knowsAbout: [
    "Luxury Consulting",
    "Luxury Training",
    "Luxury Brand Strategy",
    "Luxury Hospitality",
    "Luxury Retail",
    "Luxury Yachting",
    "Luxury Automotive",
    "Customer Experience",
    "Service Excellence",
    "Etiquette & Protocol",
    "Executive Leadership",
    "Cultural Strategy",
    "Savoir-Faire",
    "Art de Vivre",
    "Luxury Tourism",
    "Private Clienteling",
  ],
  serviceType: [
    "Luxury Advisory",
    "Strategic Consulting",
    "Executive Training",
    "Brand Strategy",
    "Customer Experience Design",
    "Service Excellence Programmes",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Next in Luxury — Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Luxury Consulting",
          description:
            "Strategic advisory, brand strategy, customer experience and operational transformation for exceptional luxury brands.",
          url: `${SITE_URL}/services/consulting`,
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Luxury Training",
          description:
            "Executive programmes, service excellence, savoir-faire transmission, leadership and team development for the luxury industry.",
          url: `${SITE_URL}/services/training`,
        },
      },
    ],
  },
  sameAs: [],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "hello@nextinluxury.com",
      availableLanguage: ["English", "French", "Italian"],
      areaServed: "Worldwide",
    },
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  inLanguage: "en",
  publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function serviceSchema({ name, description, path, category }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    category,
    url: `${SITE_URL}${path}`,
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: DEFAULT_OG_IMAGE,
    },
    areaServed: "Worldwide",
    serviceType: category,
  };
}

export function personSchema({
  name,
  slug,
  role,
  bio,
  image,
  location,
  disciplines = [],
  company,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name,
    jobTitle: role,
    description: bio,
    image,
    url: `${SITE_URL}/expert-minds/${slug}`,
    knowsAbout: disciplines,
    worksFor: company
      ? { "@type": "Organization", name: company }
      : { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    homeLocation: location
      ? { "@type": "Place", name: location }
      : undefined,
    affiliation: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  };
}
